import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import PremiumGate from '@/components/PremiumGate';
import VoiceRecorderHybrid from '@/components/VoiceRecorderWhisper';
import InvestmentsSection from '@/components/InvestmentsSection';
import TransactionModal from '@/components/TransactionModal';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wallet, TrendingUp, TrendingDown, PiggyBank, ArrowUpRight, ArrowDownRight, Calendar, Plus, Sparkles, Gem, Bell, Check, CreditCard, Eye, EyeOff, ChevronRight, ShieldAlert, Lock, Package, ChevronDown, ChevronUp, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/utils/currencyFormat';
import { useIsMobile } from '@/hooks/useIsMobile';
import MobileDashboard from '@/components/MobileDashboard';


// ─── PaymentFailedBanner ────────────────────────────────────────────────────
const PaymentFailedBanner = ({ user, token }) => {
  const [portalLoading, setPortalLoading] = useState(false);

  if (user?.subscription_status !== 'past_due') return null;

  const attempt = user?.payment_failed_count || 1;

  // Paleta escalada por intento
  const config = attempt <= 1
    ? {
        bg: 'bg-amber-50',
        border: 'border-amber-400',
        icon: <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />,
        title: 'No pudimos procesar tu pago',
        desc: 'Verifica que tu tarjeta tenga fondos disponibles o actualiza tu forma de pago.',
        badge: 'bg-amber-100 text-amber-700',
        btnClass: 'bg-amber-500 hover:bg-amber-600 active:scale-95',
      }
    : attempt === 2
    ? {
        bg: 'bg-red-50',
        border: 'border-red-400',
        icon: <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />,
        title: 'Segundo intento fallido — actualiza tu tarjeta',
        desc: 'Tu acceso Premium continuará activo mientras resuelves el pago.',
        badge: 'bg-red-100 text-red-700',
        btnClass: 'bg-red-500 hover:bg-red-600 active:scale-95',
      }
    : {
        bg: 'bg-red-50',
        border: 'border-red-600',
        icon: <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />,
        title: `Intento ${attempt} — Tu acceso está por cancelarse`,
        desc: 'Actualiza tu forma de pago antes de que Stripe cancele tu suscripción automáticamente.',
        badge: 'bg-red-200 text-red-800',
        btnClass: 'bg-red-600 hover:bg-red-700 active:scale-95',
      };

  const handleUpdateCard = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(
        `${API}/subscriptions/create-portal-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error('No se pudo abrir el portal. Intenta desde Suscripción.');
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div
      className={`flex flex-col sm:flex-row items-start sm:items-center gap-3 px-4 py-3.5 rounded-2xl border-2 ${config.bg} ${config.border} shadow-sm mb-2 animate-in slide-in-from-top-4 duration-300`}
    >
      {config.icon}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-slate-800 text-sm">{config.title}</p>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config.badge}`}>
            Intento {attempt} de 4
          </span>
        </div>
        <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">{config.desc}</p>
      </div>
      <button
        onClick={handleUpdateCard}
        disabled={portalLoading}
        className={`flex-shrink-0 flex items-center gap-1.5 text-white text-xs font-bold px-4 py-2 rounded-xl transition-all duration-150 ${config.btnClass} disabled:opacity-60`}
      >
        <CreditCard className="w-3.5 h-3.5" />
        {portalLoading ? 'Cargando...' : 'Actualizar tarjeta'}
      </button>
    </div>
  );
};

// ─── NotificationBanner ─────────────────────────────────────────────────────
const NotificationBanner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onboardingDone = localStorage.getItem('pgastos_onboarding_done') === 'true';
    if (onboardingDone && 'Notification' in window && Notification.permission === 'default' && !localStorage.getItem('pgastos_dismissed_notif_alert')) {
      // Mostrar solo después de un pequeño delay para no encimar con nada
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('pgastos_dismissed_notif_alert', 'true');
    setShow(false);
  };

  const handleEnable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        setShow(false);
        toast.success('¡Notificaciones activadas!');
        // Opcional: recargar para asegurar registro de SW
        window.location.reload();
      } else {
        toast.error('Permiso denegado. Puedes activarlo en los ajustes de tu navegador.');
        setShow(false);
      }
    } catch (err) {
      console.error('Error requesting permission:', err);
      toast.error('No se pudo solicitar el permiso');
    }
  };

  if (!show) return null;

  return (
    <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-4 sm:p-6 mb-6 text-white shadow-lg relative overflow-hidden flex flex-col sm:flex-row items-center gap-4 sm:gap-6 animate-in slide-in-from-top-4">
      <div className="absolute -right-10 -top-10 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl"></div>
      <div className="bg-white/20 p-3 rounded-full flex-shrink-0">
        <Bell className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 text-center sm:text-left">
        <h3 className="font-bold text-lg mb-1">¡No te pierdas de nada!</h3>
        <p className="text-sm text-purple-100">Activa las notificaciones para recibir recordatorios de pagos importantes y alertas de presupuesto. Prometemos no enviar spam.</p>
      </div>
      <div className="flex items-center gap-3 w-full sm:w-auto">
        <button onClick={handleDismiss} className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium hover:bg-white/10 rounded-lg transition-colors">
          Quizás luego
        </button>
        <button onClick={handleEnable} className="flex-1 sm:flex-none px-4 py-2 bg-white text-purple-600 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors shadow-sm">
          Activar ahora
        </button>
      </div>
    </div>
  );
};
const UpgradeBanner = () => {
  const { token } = useContext(AuthContext);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('pending_subscription') || sessionStorage.getItem('pending_subscription');
      if (raw) setPending(JSON.parse(raw));
    } catch {}
  }, []);

  const handleActivate = async () => {
    if (!pending || !token) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/subscriptions/create-checkout-session`,
        { price_id: pending.priceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.removeItem('pending_subscription');
      sessionStorage.removeItem('pending_subscription');
      window.location.href = res.data.url;
    } catch {
      toast.error('No se pudo iniciar el pago. Intenta desde Configuración.');
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.removeItem('pending_subscription');
    sessionStorage.removeItem('pending_subscription');
    setPending(null);
  };

  if (!pending) return null;

  return (
    <div className="flex items-center justify-between gap-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-2">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Gem className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">
            Tienes el Plan {pending.plan} pendiente de activar
          </p>
          <p className="text-xs text-slate-500">{pending.price} MXN/mes &mdash; un clic para completar el pago</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleDismiss}
          className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded transition-colors"
        >
          Descartar
        </button>
        <button
          onClick={handleActivate}
          disabled={loading}
          className="text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
        >
          {loading ? 'Cargando...' : `Activar ${pending.plan}`}
        </button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { token, refreshUser, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [creditCardsMonthlyDue, setCreditCardsMonthlyDue] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [selectedTransactionType, setSelectedTransactionType] = useState('expense');
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [latestInsight, setLatestInsight] = useState(null);
  const [desktopViewMode, setDesktopViewMode] = useState('period');
  const [expandedTxId, setExpandedTxId] = useState(null);
  const [hideBalances, setHideBalances] = useState(() => {
    try { return localStorage.getItem('hideBalances') === 'true'; } catch { return false; }
  });
  const [isCreditCardsCollapsed, setIsCreditCardsCollapsed] = useState(() => {
    try { return localStorage.getItem('hide_credit_cards_monthly_due') === 'true'; } catch { return false; }
  });

  const toggleCreditCardsCollapse = () => {
    setIsCreditCardsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('hide_credit_cards_monthly_due', String(next)); } catch {}
      return next;
    });
  };

  const toggleHideBalances = () => {
    const next = !hideBalances;
    setHideBalances(next);
    try {
      localStorage.setItem('hideBalances', String(next));
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('hide-balances-changed', { detail: next }));
    } catch {}
  };

  useEffect(() => {
    const handler = () => {
      try { setHideBalances(localStorage.getItem('hideBalances') === 'true'); } catch {}
    };
    window.addEventListener('storage', handler);
    window.addEventListener('hide-balances-changed', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('hide-balances-changed', handler);
    };
  }, []);

  const fetchLatestInsight = async () => {
    try {
      const response = await fetch(`${API}/ai/insights/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success && data.insight) {
        setLatestInsight(data.insight);
      }
    } catch (e) {
      console.warn('Latest insight fetch skipped:', e);
    }
  };

  const fetchCreditCardsMonthlyDue = async () => {
    try {
      const response = await axios.get(`${API}/accounts/credit-cards-monthly-due`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditCardsMonthlyDue(response.data || []);
    } catch (e) {
      console.warn('Error fetching credit cards monthly due:', e);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchAvailableMonths();
    fetchAccounts();
    fetchDebts();
    fetchCreditCardsMonthlyDue();
    fetchRecentTransactions();
    fetchLatestInsight();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  const fetchDebts = async () => {
    try {
      const response = await axios.get(`${API}/debts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebts(response.data || []);
    } catch (e) {
      console.warn('Error fetching debts:', e);
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(response.data) ? response.data : [];
      const sorted = [...list].sort((a, b) => {
        const dateA = a.date ? a.date.split('T')[0] : '';
        const dateB = b.date ? b.date.split('T')[0] : '';
        if (dateB !== dateA) return dateB.localeCompare(dateA);
        const timeA = new Date(a.created_at || a.date).getTime() || 0;
        const timeB = new Date(b.created_at || b.date).getTime() || 0;
        return timeB - timeA;
      });
      setRecentTransactions(sorted);
    } catch (e) {
      console.warn('Error fetching recent transactions:', e);
    }
  };
  // Prevents timezone issues when displaying dates
  const parseLocalDate = (dateString) => {
    if (!dateString) return new Date();

    // Handle ISO format with time (e.g., "2025-11-01T00:00:00.000Z")
    if (dateString.includes('T')) {
      dateString = dateString.split('T')[0];
    }

    const parts = dateString.split('-');
    if (parts.length !== 3) return new Date(dateString); // Fallback

    const [year, month, day] = parts.map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return new Date(dateString); // Fallback

    return new Date(year, month - 1, day);
  };

  // Get current month and year
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  // Refresh user data on mount and periodically
  useEffect(() => {
    if (refreshUser) {
      refreshUser(); // Initial refresh

      // Refresh every 30 seconds to catch subscription updates
      const interval = setInterval(() => {
        refreshUser();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [refreshUser]);

  useEffect(() => {
    fetchStats();
    fetchAvailableMonths();
    fetchAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  const fetchAvailableMonths = async () => {
    try {
      // Fetch all transactions to determine which months have data
      const response = await axios.get(`${API}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Extract unique months from transactions
      const monthsSet = new Set();
      response.data.forEach(transaction => {
        const date = parseLocalDate(transaction.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
        monthsSet.add(monthKey);
      });

      // Convert to array and sort (most recent first)
      const monthsArray = Array.from(monthsSet).sort((a, b) => {
        const [yearA, monthA] = a.split('-').map(Number);
        const [yearB, monthB] = b.split('-').map(Number);
        if (yearA !== yearB) return yearB - yearA;
        return monthB - monthA;
      });

      // Format for display
      const formattedMonths = monthsArray.map(monthKey => {
        const [year, month] = monthKey.split('-').map(Number);
        const date = new Date(year, month - 1, 1);
        const monthName = date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
        return {
          value: monthKey,
          label: monthName.charAt(0).toUpperCase() + monthName.slice(1)
        };
      });

      setAvailableMonths(formattedMonths);
    } catch (error) {
    }
  };

  const fetchStats = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let url = `${API}/statistics`;

      // Add query parameters based on selected period
      if (selectedPeriod === 'current') {
        url += `?month=${currentMonth}&year=${currentYear}`;
      } else if (selectedPeriod !== 'all') {
        // It's a specific month in format 'YYYY-M'
        const [year, month] = selectedPeriod.split('-');
        url += `?month=${month}&year=${year}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleQuickAdd = (type) => {
    // Open modal with pre-selected type
    setSelectedTransactionType(type);
    setIsTransactionModalOpen(true);
  };

  const refreshAllDashboardData = async (silent = true) => {
    await Promise.allSettled([
      fetchStats(silent),
      fetchAccounts(),
      fetchDebts(),
      fetchCreditCardsMonthlyDue(),
      fetchRecentTransactions()
    ]);
  };

  const handleTransactionSuccess = () => {
    refreshAllDashboardData(true);
    window.dispatchEvent(new CustomEvent('transaction-added'));
  };

  // Listener para refrescar datos automáticamente cuando se agrega/elimina una transacción
  useEffect(() => {
    const handleTransactionAdded = () => {
      refreshAllDashboardData(true);
    };

    window.addEventListener('transaction-added', handleTransactionAdded);
    return () => {
      window.removeEventListener('transaction-added', handleTransactionAdded);
    };
  }, [selectedPeriod, token]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Cargando...</div>
        </div>
      </Layout>
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '¡Buenos días!';
    if (hour < 18) return '¡Buenas tardes!';
    return '¡Buenas noches!';
  };

  if (isMobile) {
    return (
      <Layout>
        <div className="px-4 pt-4">
          <UpgradeBanner />
        </div>
        <MobileDashboard
          stats={stats}
          accounts={accounts}
          debts={debts}
          creditCardsMonthlyDue={creditCardsMonthlyDue}
          recentTransactions={recentTransactions}
          loading={loading}
          selectedPeriod={selectedPeriod}
          availableMonths={availableMonths}
          latestInsight={latestInsight}
          onPeriodChange={setSelectedPeriod}
          onRefresh={() => { fetchStats(); fetchAccounts(); fetchDebts(); fetchCreditCardsMonthlyDue(); fetchRecentTransactions(); }}
          onAddTransaction={(type) => {
            window.dispatchEvent(new CustomEvent('open-transaction-modal', { detail: { type } }));
          }}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 px-4 py-6 md:px-8 md:py-10 max-w-7xl mx-auto" data-testid="dashboard-container">
        <PaymentFailedBanner user={user} token={token} />
        <UpgradeBanner />
        <NotificationBanner />
        
        {/* Header Section con Bienvenida */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-indigo-600 font-bold tracking-tight">
              <Sparkles className="w-5 h-5 animate-pulse" />
              <span className="uppercase text-xs tracking-widest">{getGreeting()}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
              Hola, <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">{user?.nickname || user?.username || 'Usuario'}</span>
            </h1>
            <p className="text-slate-500 font-medium">Aquí tienes el resumen de tu mundo financiero.</p>
          </div>

          {/* Controles de Vista Desktop: Selector de Período */}
          <div className="flex items-center gap-3 self-start md:self-auto">
            {/* Botón para ocultar/mostrar saldos */}
            <button
              type="button"
              onClick={toggleHideBalances}
              className="p-2.5 rounded-2xl bg-white border border-slate-100 shadow-sm hover:bg-slate-50 transition-all text-slate-500 hover:text-slate-800 active:scale-95 duration-100 flex items-center justify-center cursor-pointer"
              title={hideBalances ? "Mostrar saldos" : "Ocultar saldos"}
            >
              {hideBalances ? <EyeOff className="w-4 h-4 text-indigo-600" /> : <Eye className="w-4 h-4 text-slate-500" />}
            </button>

            {/* Selector de Período */}
            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl shadow-sm border border-slate-100">
              <Calendar className="w-4 h-4 text-slate-400 ml-2" />
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-[160px] border-none shadow-none focus:ring-0 font-bold text-xs text-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-100">
                  <SelectItem value="current">Este Mes</SelectItem>
                  <SelectItem value="all">Todo el Tiempo</SelectItem>
                  {availableMonths
                    .filter(option => {
                      const currentMonthKey = `${currentYear}-${currentMonth}`;
                      return option.value !== currentMonthKey;
                    })
                    .map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Tarjeta Unificada de Balance Desktop - Idéntica a Móvil con adaptación panorámica */}
        {(() => {
          const savingsAccountsTotal = (accounts || []).reduce((sum, a) => {
            if (a.type === 'credit_card' || a.is_active === false || !a.has_yield) return sum;
            const amt = a.currency === 'USD' ? (a.current_balance || 0) * 17 : (a.current_balance || 0);
            return sum + amt;
          }, 0);
          const liquidAssets = (accounts || []).reduce((sum, a) => {
            if (a.type === 'credit_card' || a.is_active === false || a.has_yield) return sum;
            const amt = a.currency === 'USD' ? (a.current_balance || 0) * 17 : (a.current_balance || 0);
            return sum + amt;
          }, 0);
          const activeDebts = (debts || []).filter(d => d.status === 'active');
          const totalDebt = activeDebts.reduce((sum, d) => sum + (d.current_balance || 0), 0);
          const realNetWorth = (liquidAssets + savingsAccountsTotal) - totalDebt;
          const netBalance = (stats?.total_income || 0) - (stats?.total_expenses || 0);
          const totalSavings = (stats?.total_savings || 0) + savingsAccountsTotal;

          const isPeriodPositive = netBalance >= 0;
          const isNetWorthPositive = realNetWorth >= 0;
          const isCurrentPositive = desktopViewMode === 'period' ? isPeriodPositive : isNetWorthPositive;

          return (
            <Card className="p-6 lg:p-7 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 border border-slate-800 shadow-2xl relative overflow-hidden group rounded-[2.2rem] text-white mb-8">
              {/* Background Glow */}
              <div className={`absolute -right-8 -top-8 w-64 h-64 rounded-full blur-3xl pointer-events-none transition-all duration-700 ${
                isCurrentPositive ? 'bg-emerald-500/15 group-hover:bg-emerald-500/25' : 'bg-rose-500/15 group-hover:bg-rose-500/25'
              }`} />

              <div className="relative z-10 space-y-6">
                {/* Barra superior de la tarjeta: Pill Switcher + Ojito + Badge de Estado */}
                <div className="flex items-center justify-between gap-3">
                  {/* Pill Switcher */}
                  <div className="bg-slate-800/90 p-1 rounded-xl flex items-center border border-slate-700/60 shadow-inner">
                    <button
                      type="button"
                      onClick={() => setDesktopViewMode('period')}
                      className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${
                        desktopViewMode === 'period'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      Flujo del Período
                    </button>
                    <button
                      type="button"
                      onClick={() => setDesktopViewMode('networth')}
                      className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${
                        desktopViewMode === 'networth'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      Patrimonio Real
                    </button>
                  </div>

                  {/* Controles derechos: Ojito + Tag de Estado */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={toggleHideBalances}
                      className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-white transition-colors border border-slate-700/50 shadow-sm"
                      title={hideBalances ? "Mostrar cantidades" : "Ocultar cantidades"}
                    >
                      {hideBalances ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4 text-slate-200" />}
                    </button>

                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider shadow-sm border ${
                      isCurrentPositive 
                        ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' 
                        : 'bg-rose-500/15 text-rose-400 border-rose-500/30 animate-pulse'
                    }`}>
                      {isCurrentPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                      {desktopViewMode === 'period'
                        ? (netBalance >= 0 ? 'Superávit' : 'Déficit')
                        : (realNetWorth >= 0 ? 'Positivo' : 'Déficit Total')}
                    </span>
                  </div>
                </div>

                {/* Contenido en 2 columnas: Gran Resultado a la izquierda + 3 Sub-tarjetas anidadas a la derecha */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center pt-1">
                  {/* Columna Izquierda: Resultado Principal (5 columnas) */}
                  <div className="lg:col-span-5 space-y-2">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Wallet className="w-4 h-4 text-indigo-400" />
                      <span className="text-xs font-bold uppercase tracking-wider">
                        {desktopViewMode === 'period' ? 'Resultado Neto del Período' : 'Patrimonio Neto Real'}
                      </span>
                    </div>

                    <p className={`text-4xl lg:text-5xl font-black tracking-tight ${
                      isCurrentPositive ? 'text-white' : 'text-rose-400'
                    }`}>
                      {hideBalances 
                        ? '••••••' 
                        : (desktopViewMode === 'period'
                            ? (netBalance >= 0 ? `+${formatCurrency(netBalance)}` : formatCurrency(netBalance))
                            : formatCurrency(realNetWorth))}
                    </p>

                    <p className="text-xs font-medium text-slate-400 leading-relaxed max-w-md">
                      {desktopViewMode === 'period'
                        ? (netBalance >= 0 
                            ? 'Tus ingresos superaron tus gastos en el período seleccionado.' 
                            : 'Tus gastos excedieron tus ingresos en este período.')
                        : (realNetWorth >= 0 
                            ? 'Tus activos totales cubren la totalidad de tus deudas.' 
                            : 'Tus deudas superan el total de tus activos disponibles.')}
                    </p>
                  </div>

                  {/* Columna Derecha: 3 Tarjetas Anidadas como en Móvil (7 columnas) */}
                  <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {desktopViewMode === 'period' ? (
                      <>
                        {/* Ingresos Período */}
                        <div 
                          onClick={() => handleQuickAdd('income')}
                          className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/40 hover:border-slate-600 hover:bg-slate-800/70 transition-all cursor-pointer group/card flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex items-center justify-between gap-1 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                                Ingresos Período
                              </span>
                              <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                            </div>
                            <p className="text-xl font-black text-emerald-400 mt-2 truncate">
                              {hideBalances ? '••••••' : formatCurrency(stats?.total_income || 0)}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 mt-2 block">Entradas del período</span>
                        </div>

                        {/* Gastos Período */}
                        <div 
                          onClick={() => handleQuickAdd('expense')}
                          className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/40 hover:border-slate-600 hover:bg-slate-800/70 transition-all cursor-pointer group/card flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex items-center justify-between gap-1 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-rose-400"></span>
                                Gastos Período
                              </span>
                              <ArrowDownRight className="w-3.5 h-3.5 text-rose-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                            </div>
                            <p className="text-xl font-black text-rose-400 mt-2 truncate">
                              {hideBalances ? '••••••' : formatCurrency(stats?.total_expenses || 0)}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 mt-2 block">Salidas del período</span>
                        </div>

                        {/* Ahorros Consolidados */}
                        <div 
                          onClick={() => handleQuickAdd('saving')}
                          className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/40 hover:border-slate-600 hover:bg-slate-800/70 transition-all cursor-pointer group/card flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex items-center justify-between gap-1 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                Ahorros
                              </span>
                              <Check className="w-3.5 h-3.5 text-blue-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                            </div>
                            <p className="text-xl font-black text-blue-400 mt-2 truncate">
                              {hideBalances ? '••••••' : formatCurrency(totalSavings)}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 mt-2 block">Apartados y rendimiento</span>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Dinero Disponible */}
                        <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/40 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                              Dinero Disponible
                            </div>
                            <p className="text-xl font-black text-emerald-400 mt-2 truncate">
                              {hideBalances ? '••••••' : formatCurrency(liquidAssets)}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 mt-2 block">Bancos y efectivo</span>
                        </div>

                        {/* Deudas Totales */}
                        <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/40 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              <span className="w-2 h-2 rounded-full bg-rose-400"></span>
                              Deudas Totales
                            </div>
                            <p className="text-xl font-black text-rose-400 mt-2 truncate">
                              {hideBalances ? '••••••' : `-${formatCurrency(totalDebt)}`}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 mt-2 block">Tarjetas y préstamos</span>
                        </div>

                        {/* Ahorro con Rendimiento */}
                        <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-700/40 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              <span className="w-2 h-2 rounded-full bg-indigo-400"></span>
                              Ahorro e Inversión
                            </div>
                            <p className="text-xl font-black text-indigo-400 mt-2 truncate">
                              {hideBalances ? '••••••' : formatCurrency(savingsAccountsTotal)}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 mt-2 block">Cuentas con rendimiento</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })()}

        {/* Card Resumen: Activos vs Deuda Actual */}
        {(() => {
          const totalAssets = (accounts || []).reduce((sum, a) => {
            if (a.type === 'credit_card' || !a.is_active) return sum;
            return sum + (a.currency === 'USD' ? (a.current_balance || 0) * 17 : (a.current_balance || 0));
          }, 0);
          const totalCreditDebt = (accounts || []).reduce((sum, a) => {
            if (a.type !== 'credit_card' || !a.is_active) return sum;
            return sum + Math.abs(a.current_balance || 0);
          }, 0);
          const activeDebts = (debts || []).filter(d => d.status === 'active');
          const totalIOwe = activeDebts.filter(d => d.debt_direction === 'i_owe').reduce((s, d) => s + (d.current_balance || 0), 0);
          const totalOwedToMe = activeDebts.filter(d => d.debt_direction === 'they_owe_me').reduce((s, d) => s + (d.current_balance || 0), 0);
          const deudaTotal = totalCreditDebt + totalIOwe;
          const covered = totalAssets >= deudaTotal;
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1">Activos Totales</p>
                <p className="text-xl font-black text-emerald-700 dark:text-emerald-300">{hideBalances ? '••••••' : formatCurrency(totalAssets)}</p>
                <p className="text-[10px] text-emerald-600/70 mt-0.5">Cuentas activas</p>
              </div>
              <div className="p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 rounded-2xl">
                <p className="text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 mb-1">Deuda Tarjetas</p>
                <p className="text-xl font-black text-rose-700 dark:text-rose-300">{hideBalances ? '••••••' : `-${formatCurrency(totalCreditDebt)}`}</p>
                <p className="text-[10px] text-rose-600/70 mt-0.5">Por pagar a bancos</p>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-2xl">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1">Deuda Personal Neta</p>
                <p className="text-xl font-black text-amber-700 dark:text-amber-300">{hideBalances ? '••••••' : `-${formatCurrency(Math.max(0, totalIOwe - totalOwedToMe))}`}</p>
                <p className="text-[10px] text-amber-600/70 mt-0.5">Yo debo − Me deben</p>
              </div>
              <div className={`p-4 rounded-2xl border ${
                covered
                  ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                  : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800/50'
              }`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1">¿Activos cubren todo?</p>
                <p className={`text-xl font-black ${covered ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {covered ? '✅ Sí' : '⚠️ No'}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {hideBalances ? '••••••' : `Deuda total: -${formatCurrency(deudaTotal)}`}
                </p>
              </div>
            </div>
          );
        })()}

        {/* Main Grid: 2 Columns for Desktop Space Optimization */}
        {(() => {
          const myDebts = (debts || []).filter(d => d.status === 'active' && d.debt_direction !== 'they_owe_me');
          const theyOweMe = (debts || []).filter(d => d.status === 'active' && d.debt_direction === 'they_owe_me');
          const activeDebts = [...myDebts, ...theyOweMe];
          const totalDebtsPending = myDebts.reduce((s, d) => s + (d.current_balance || 0), 0);
          const totalReceivablePending = theyOweMe.reduce((s, d) => s + (d.current_balance || 0), 0);
          const hasAnyDebts = myDebts.length > 0 || theyOweMe.length > 0;

          return (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left / Main Column (8 cols) */}
              <div className="lg:col-span-8 space-y-8">
                
                {/* Punto 42: Widget Desktop - Pagos del Mes en Tarjetas (Colapsable) */}
                {creditCardsMonthlyDue && creditCardsMonthlyDue.length > 0 && (() => {
                  const totalCardsDue = creditCardsMonthlyDue.reduce((sum, c) => sum + (c.urgency_status === 'paid' ? 0 : (c.total_due || 0)), 0);
                  const unpaidCount = creditCardsMonthlyDue.filter(c => c.urgency_status !== 'paid' && c.current_debt > 0.01).length;

                  return (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between px-1">
                        <button
                          type="button"
                          onClick={toggleCreditCardsCollapse}
                          className="flex items-center gap-3 text-left group"
                        >
                          <div className="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 flex items-center justify-center shrink-0">
                            <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div className="flex items-center gap-2.5 flex-wrap">
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Pagos del Mes en Tarjetas</h3>
                                {unpaidCount > 0 && (
                                  <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60 px-2.5 py-0.5 rounded-full border border-indigo-200/60 dark:border-indigo-800/60">
                                    {hideBalances ? '•••' : formatCurrency(totalCardsDue, 'MXN')}
                                  </span>
                                )}
                                {isCreditCardsCollapsed ? (
                                  <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-transform" />
                                ) : (
                                  <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-transform" />
                                )}
                              </div>
                              <p className="text-xs text-slate-400 font-medium">
                                Cuotas de MSI activas, compras del período y ajustes de saldo pendiente
                              </p>
                            </div>
                          </div>
                        </button>
                        <Button
                          variant="ghost"
                          onClick={() => navigate('/accounts')}
                          className="text-indigo-600 hover:text-indigo-700 font-bold text-sm"
                        >
                          Ver cuentas
                        </Button>
                      </div>

                      {!isCreditCardsCollapsed && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {creditCardsMonthlyDue.map((card) => {
                            const isUrgent = card.urgency_status === 'today' || (card.days_until_due !== null && card.days_until_due <= 3 && card.days_until_due >= 0);
                            const isPaid = card.urgency_status === 'paid' || card.current_debt <= 0.01;

                            return (
                              <div
                                key={card.account_id}
                                className={`p-5 rounded-3xl border transition-all ${
                                  isPaid
                                    ? 'bg-slate-50/80 dark:bg-slate-800/40 border-slate-200/60 dark:border-slate-700/60 opacity-80'
                                    : isUrgent
                                    ? 'bg-gradient-to-br from-rose-50/80 via-white to-pink-50/40 dark:from-rose-950/20 dark:to-slate-800 border-rose-200/80 dark:border-rose-900/40 shadow-md shadow-rose-500/5'
                                    : 'bg-white dark:bg-slate-800 border-slate-200/80 dark:border-slate-700 shadow-sm'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-black text-slate-900 dark:text-white text-base">
                                        {card.account_name}
                                      </h4>
                                      {card.bank_name && (
                                        <span className="text-xs text-slate-400 font-medium">
                                          • {card.bank_name}
                                        </span>
                                      )}
                                      {card.has_reminder && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                                          🔔 Recordatorio
                                        </span>
                                      )}
                                    </div>

                                    <div className="mt-1.5">
                                      {isPaid ? (
                                        <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-0.5 rounded-lg border border-emerald-200/60">
                                          <Check className="w-3.5 h-3.5" /> Al corriente
                                        </span>
                                      ) : card.days_until_due !== null ? (
                                        <span className={`inline-flex items-center gap-1.5 text-xs font-black px-2.5 py-0.5 rounded-lg border ${
                                          card.days_until_due === 0
                                            ? 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 animate-pulse'
                                            : card.days_until_due <= 3
                                            ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300'
                                            : 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300'
                                        }`}>
                                          <Calendar className="w-3.5 h-3.5" />
                                          {card.days_until_due === 0 ? '🔴 Vence hoy' : card.days_until_due === 1 ? '⚡ Vence mañana' : `⏳ Vence en ${card.days_until_due} días`} ({card.due_date_formatted})
                                        </span>
                                      ) : (
                                        <span className="text-xs text-slate-400 font-medium">
                                          Sin fecha límite configurada
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="text-right">
                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Pago del Mes</p>
                                    <p className={`text-xl font-black ${
                                      isPaid ? 'text-slate-400 line-through' : 'text-slate-900 dark:text-white'
                                    }`}>
                                      {hideBalances ? '••••••' : formatCurrency(card.total_due, card.currency)}
                                    </p>
                                  </div>
                                </div>

                                {/* Mini desglose */}
                                <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 overflow-x-auto text-[11px] text-slate-500">
                                    {card.msi_month_payment > 0 && (
                                      <span className="bg-slate-100 dark:bg-slate-700/60 px-2 py-0.5 rounded-md font-semibold text-slate-700 dark:text-slate-300">
                                        🏷️ MSI: {hideBalances ? '•••' : formatCurrency(card.msi_month_payment, card.currency)}
                                      </span>
                                    )}
                                    {card.non_msi_debt > 0 && (
                                      <span className="bg-slate-100 dark:bg-slate-700/60 px-2 py-0.5 rounded-md font-semibold text-slate-700 dark:text-slate-300">
                                        🛒 Compras: {hideBalances ? '•••' : formatCurrency(card.non_msi_debt, card.currency)}
                                      </span>
                                    )}
                                    {card.unbroken_adj !== 0 && (
                                      <span className="bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-md font-semibold border border-amber-200/50">
                                        ➕ Ajuste: {hideBalances ? '•••' : formatCurrency(card.unbroken_adj, card.currency)}
                                      </span>
                                    )}
                                  </div>

                                  <Button
                                    size="sm"
                                    onClick={() => navigate(`/accounts/${card.account_id}`)}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shrink-0 rounded-xl"
                                  >
                                    Pagar
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Widget: Control de Deudas (Debo vs Me deben) */}
                {hasAnyDebts && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center">
                          <CreditCard className="w-4 h-4 text-rose-500" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">Control de Deudas</h3>
                          <p className="text-xs text-slate-400 font-medium">
                            Debes: <span className="font-bold text-rose-600">{formatCurrency(totalDebtsPending)}</span> • Te deben: <span className="font-bold text-emerald-600">{formatCurrency(totalReceivablePending)}</span>
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        onClick={() => navigate('/debts')}
                        className="text-indigo-600 hover:text-indigo-700 font-bold text-sm"
                      >
                        Gestionar todas
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Tarjeta 1: Lo que debo */}
                      <div
                        onClick={() => navigate('/debts?tab=i_owe')}
                        className="p-5 rounded-3xl bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-md shadow-rose-500/15 cursor-pointer hover:scale-[1.01] transition-all space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold uppercase tracking-wider text-white/80">🔴 Mis Deudas (Por pagar)</span>
                          <span className="text-xs font-bold px-2 py-0.5 bg-white/20 rounded-full">{myDebts.length} activas</span>
                        </div>
                        <p className="text-2xl font-black">{formatCurrency(totalDebtsPending)}</p>
                        <p className="text-[11px] text-white/80">Pagos, tarjetas y préstamos personales</p>
                      </div>

                      {/* Tarjeta 2: Lo que me deben */}
                      <div
                        onClick={() => navigate('/debts?tab=they_owe_me')}
                        className="p-5 rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-500/15 cursor-pointer hover:scale-[1.01] transition-all space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold uppercase tracking-wider text-white/80">🟢 Me Deben (Por cobrar)</span>
                          <span className="text-xs font-bold px-2 py-0.5 bg-white/20 rounded-full">{theyOweMe.length} activas</span>
                        </div>
                        <p className="text-2xl font-black">{formatCurrency(totalReceivablePending)}</p>
                        <p className="text-[11px] text-white/80">Préstamos a terceros y compras compartidas</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {activeDebts.slice(0, 2).map((debt) => {
                        const effectiveTotal = Math.max(debt.total_amount || 0, debt.current_balance || 0);
                        const paid = Math.max(effectiveTotal - (debt.current_balance || 0), 0);
                        const progress = effectiveTotal > 0 ? Math.min(Math.max((paid / effectiveTotal) * 100, 0), 100) : 0;
                        const targetTab = debt.debt_direction === 'they_owe_me' ? 'they_owe_me' : 'i_owe';
                        return (
                          <div
                            key={debt.id}
                            onClick={() => navigate(`/debts?tab=${targetTab}&debtId=${debt.id}`)}
                            className="p-5 rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer group space-y-3"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${debt.color || '#6366f1'}18` }}>
                                  <CreditCard className="w-4 h-4" style={{ color: debt.color || '#6366f1' }} />
                                </div>
                                <div className="min-w-0">
                                  <p className="font-bold text-slate-900 text-sm truncate">{debt.name}</p>
                                  <p className="text-[10px] text-slate-400 font-semibold uppercase">{debt.creditor_type || 'Deuda'}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-black text-slate-900 text-sm">{formatCurrency(debt.current_balance || 0)}</p>
                                <p className="text-[10px] text-slate-400 font-medium">{debt.has_interest ? 'por liquidar' : 'restante'}</p>
                              </div>
                            </div>

                            {debt.has_interest && (
                              <div className="grid grid-cols-2 gap-1.5 bg-amber-50/70 border border-amber-100/80 rounded-xl p-2 text-[10px]">
                                <div>
                                  <span className="text-slate-400 block font-semibold">Capital</span>
                                  <span className="font-black text-slate-800">{formatCurrency(debt.current_principal || 0)}</span>
                                </div>
                                <div>
                                  <span className="text-amber-700/80 block font-semibold">Interés ({debt.interest_rate}%)</span>
                                  <span className="font-black text-amber-800">{formatCurrency(debt.accumulated_interest || 0)}</span>
                                </div>
                              </div>
                            )}

                            <div className="space-y-1">
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: debt.color || '#e11d48' }} />
                              </div>
                              <p className="text-[10px] text-slate-400 text-right font-bold">{progress.toFixed(0)}% pagado</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Widget: Mis Cuentas (Solo si hay cuentas) */}
                {accounts.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <h3 className="text-xl font-bold text-slate-900">Mis Cuentas</h3>
                      <Button
                        variant="ghost"
                        onClick={() => navigate('/accounts')}
                        className="text-indigo-600 hover:text-indigo-700 font-bold text-sm"
                      >
                        Ver todas ({accounts.length})
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {accounts.slice(0, 3).map((account) => (
                        <div
                          key={account.id}
                          className="p-5 rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer group relative overflow-hidden"
                          onClick={() => navigate(`/accounts/${account.id}`)}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div
                              className="w-10 h-10 rounded-2xl flex items-center justify-center"
                              style={{ backgroundColor: `${account.color}15` }}
                            >
                              <Wallet className="w-5 h-5" style={{ color: account.color }} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-900 text-sm truncate">{account.name}</p>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{account.currency}</p>
                            </div>
                          </div>
                          <p className="text-xl font-black tracking-tight" style={{ color: account.color }}>
                            {formatCurrency(account.current_balance)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transacciones Recientes */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between px-1">
                    <h2 className="text-xl font-bold text-slate-900">Transacciones Recientes</h2>
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/transactions')}
                      className="text-indigo-600 hover:text-indigo-700 font-bold text-sm"
                    >
                      Ver historial
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5">
                    {stats?.recent_transactions && stats.recent_transactions.length > 0 ? (
                      stats.recent_transactions.slice(0, 6).map((transaction) => {
                        const acc = accounts.find(a => a.id === transaction.account_id);
                        const isCredit = acc?.type === 'credit_card';
                        const isIncome = transaction.type === 'income';
                        const isExpense = transaction.type === 'expense';
                        const isSaving = transaction.type === 'saving';
                        const showItems = expandedTxId === transaction.id;

                        // Calcular saldo anterior y posterior exacto
                        let prevVal = transaction.previous_balance !== null && transaction.previous_balance !== undefined ? parseFloat(transaction.previous_balance) : null;
                        let nextVal = transaction.new_balance !== null && transaction.new_balance !== undefined ? parseFloat(transaction.new_balance) : null;
                        
                        if (isCredit && prevVal !== null) {
                          const amt = parseFloat(transaction.amount) || 0;
                          const prevDebt = Math.abs(prevVal);
                          prevVal = prevDebt;
                          if (transaction.is_balance_adjustment || /ajuste de saldo/i.test(transaction.description || '')) {
                            nextVal = nextVal !== null ? Math.abs(nextVal) : prevDebt;
                          } else if (transaction.is_breakdown || (transaction.is_retention && transaction.retention_status === 'released')) {
                            nextVal = prevDebt;
                          } else if (isExpense) {
                            nextVal = prevDebt + amt; // Compra incrementa la deuda
                          } else if (isIncome) {
                            nextVal = Math.max(0, prevDebt - amt); // Abono reduce la deuda
                          } else {
                            nextVal = prevDebt;
                          }
                        } else if (!isCredit && prevVal !== null && nextVal === null) {
                          const amt = parseFloat(transaction.amount) || 0;
                          nextVal = isIncome ? prevVal + amt : isExpense ? prevVal - amt : prevVal;
                        }

                        const nextColor = isExpense ? 'text-rose-500/90' : 'text-emerald-600/90';

                        return (
                          <div
                            key={transaction.id}
                            className="p-4 bg-white border border-slate-100 rounded-2xl hover:border-slate-200 hover:shadow-sm transition-all"
                          >
                            <div 
                              className="flex items-start justify-between gap-4 cursor-pointer"
                              onClick={() => navigate('/transactions')}
                            >
                              <div className="flex items-start gap-3 min-w-0">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                                  isIncome ? 'bg-green-50 text-green-600' :
                                  isExpense ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {isIncome ? (
                                    <ArrowUpRight className="w-5 h-5" />
                                  ) : isExpense ? (
                                    <ArrowDownRight className="w-5 h-5" />
                                  ) : (
                                    <PiggyBank className="w-5 h-5" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-bold text-slate-900 text-sm hover:text-indigo-600 transition-colors truncate">
                                    {transaction.description || 'Sin descripción'}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                                      {parseLocalDate(transaction.date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                                    </p>
                                    {acc && (
                                      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                        {acc.name}
                                      </span>
                                    )}
                                    {transaction.items_breakdown?.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedTxId(showItems ? null : transaction.id);
                                        }}
                                        className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full flex items-center gap-1 hover:bg-indigo-100 transition-all"
                                      >
                                        <Package className="w-3 h-3 text-indigo-600" />
                                        <span>{transaction.items_breakdown.length} artículos</span>
                                        {showItems ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                                      </button>
                                    )}
                                  </div>

                                  {/* Chip de Saldo Anterior y Posterior */}
                                  {prevVal !== null && !isNaN(prevVal) && nextVal !== null && !isNaN(nextVal) && (
                                    <div className="mt-2 px-2.5 py-0.5 bg-slate-50 rounded-lg border border-slate-100 flex items-center gap-1.5 text-[11px] w-fit">
                                      <span className="text-slate-400 opacity-80">Ant:</span>
                                      <span className="font-medium text-slate-600">{formatCurrency(prevVal)}</span>
                                      <span className="text-slate-300 font-normal">→</span>
                                      <span className="text-slate-400 opacity-80">Después:</span>
                                      <span className={`font-semibold ${nextColor}`}>{formatCurrency(nextVal)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className={`text-base font-black tracking-tight shrink-0 ${
                                isIncome ? 'text-green-600' :
                                isExpense ? 'text-red-600' : 'text-blue-600'
                              }`}>
                                {hideBalances ? '••••••' : `${isIncome ? '+' : '-'}${formatCurrency(transaction.amount)}`}
                              </div>
                            </div>

                            {/* Desglose desplegado de artículos */}
                            {showItems && transaction.items_breakdown?.length > 0 && (
                              <div className="mt-3 pt-2.5 border-t border-slate-100 space-y-1.5">
                                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                                  Artículos de esta compra ({transaction.items_breakdown.length})
                                </p>
                                <div className="space-y-1">
                                  {transaction.items_breakdown.map((item, idx) => (
                                    <div key={item.id || idx} className="flex items-center justify-between text-xs py-1 px-2.5 bg-slate-50 rounded-xl">
                                      <span className="text-slate-700 font-medium truncate mr-2">{item.name}</span>
                                      <span className="font-bold text-slate-900 shrink-0">${parseFloat(item.price).toFixed(2)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                        <p className="text-slate-400 font-bold text-sm">No hay transacciones todavía</p>
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {/* Right Column (4 cols) - Voice AI & Insights */}
              <div className="lg:col-span-4 space-y-6">
                {/* Voice AI Recorder Compacto */}
                <div className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm space-y-3">
                  <div className="flex items-center gap-2 text-indigo-600">
                    <Sparkles className="w-4 h-4" />
                    <h3 className="font-black text-slate-800 text-sm">Asistente por Voz</h3>
                  </div>
                  <VoiceRecorderHybrid onTransactionCreated={() => { fetchStats(); fetchAccounts(); }} />
                </div>

                {/* AI Insights Widget Desktop */}
                <Card className="p-1 border-none bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-xl shadow-indigo-100">
                  <div className="bg-white/95 backdrop-blur-sm rounded-[1.4rem] p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-md text-white shrink-0">
                        <Sparkles className="w-6 h-6" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-black text-slate-900 text-base">AI Insights</h4>
                        <p className="text-xs text-slate-500 font-medium">Diagnóstico inteligente con IA</p>
                      </div>
                    </div>

                    {/* Resumen ejecutivo */}
                    <div className="bg-indigo-50/50 p-3.5 rounded-2xl border border-indigo-100/70 text-xs text-slate-700 leading-relaxed font-medium">
                      💡 {latestInsight?.summary || "Análisis predictivo de tus hábitos de gasto y ahorro disponible en Análisis Pro."}
                    </div>

                    <PremiumGate
                      fallback={
                        <Button
                          onClick={() => navigate('/pricing')}
                          className="w-full py-3 bg-slate-900 text-white hover:bg-slate-800 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-1.5"
                        >
                          <Lock className="w-3.5 h-3.5 text-amber-400" />
                          <span>Desbloquear con Premium</span>
                        </Button>
                      }
                    >
                      <Button
                        type="button"
                        onClick={() => navigate('/advanced-analytics')}
                        className="w-full py-3 bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
                      >
                        <span>Ver análisis completo</span>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </PremiumGate>
                  </div>
                </Card>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Transaction Modal */}
      <TransactionModal
        isOpen={isTransactionModalOpen}
        onClose={() => setIsTransactionModalOpen(false)}
        onSuccess={handleTransactionSuccess}
        preselectedType={selectedTransactionType}
      />
    </Layout>
  );
};

export default Dashboard;